import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { paymentsApi, emailApi, authApi, scanApi, referralApi } from '../lib/api';
import { supportedCountries, supportedLanguages, countryNames } from '../lib/translations';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  User, 
  Mail, 
  Crown,
  CheckCircle,
  Loader2,
  AlertCircle,
  Zap,
  Search,
  Unplug,
  Globe,
  Plus,
  FileText,
  Upload,
  X,
  Gift,
  Copy,
  Award,
  Users,
  Trash2,
  HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const { user, updateUser } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoadingPayment, setIsLoadingPayment] = useState(false);
  const [emailConnections, setEmailConnections] = useState([]);
  const [isConnectingGmail, setIsConnectingGmail] = useState(false);
  const [scanningConnectionId, setScanningConnectionId] = useState(null);
  const [disconnectingConnectionId, setDisconnectingConnectionId] = useState(null);
  const [foundingSlots, setFoundingSlots] = useState({ total: 500, sold: 0, remaining: 500 });
  const [scanResults, setScanResults] = useState({});
  const [selectedCountry, setSelectedCountry] = useState(user?.country || 'US');
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [quickCheckStatus, setQuickCheckStatus] = useState(null);
  
  // Disconnect Gmail modal state
  const [disconnectModal, setDisconnectModal] = useState({ open: false, email: null, connectionId: null });
  
  // Bank Statement Scanner state
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [bankStatementResults, setBankStatementResults] = useState(null);
  const fileInputRef = useRef(null);

  // Referral state
  const [referralData, setReferralData] = useState(null);
  const [isLoadingReferral, setIsLoadingReferral] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Account deletion state
  const [deleteAccountModal, setDeleteAccountModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  useEffect(() => {
    // Check for payment return
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    const gmailStatus = searchParams.get('gmail');
    
    if (payment === 'success' && sessionId) {
      checkPaymentStatus(sessionId);
    } else if (payment === 'cancelled') {
      toast.info('Payment was cancelled');
    }
    
    // Check Gmail OAuth return
    if (gmailStatus === 'connected') {
      toast.success('Gmail connected successfully!');
    } else if (gmailStatus === 'error') {
      toast.error('Failed to connect Gmail. Please try again.');
    }

    loadEmailConnections();
    loadFoundingSlots();
    loadQuickCheckStatus();
  }, [searchParams]);

  const loadQuickCheckStatus = async () => {
    try {
      const response = await paymentsApi.getQuickCheckStatus();
      setQuickCheckStatus(response.data);
    } catch (error) {
      console.error('Failed to load quick check status');
    }
  };

  const loadFoundingSlots = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payments/founding-slots`);
      setFoundingSlots(response.data);
    } catch (error) {
      console.error('Failed to load founding slots');
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await paymentsApi.getStatus(sessionId);
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your plan has been upgraded.');
        
        // Track Purchase event for Meta Pixel
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Purchase', {
            content_name: response.data.plan || 'SubGuard Plan',
            content_category: 'subscription',
            value: response.data.amount || 0,
            currency: 'USD'
          });
        }
        
        // Refresh user data and quick check status
        const userResponse = await authApi.getMe();
        updateUser(userResponse.data);
        loadQuickCheckStatus();
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  };

  const loadEmailConnections = async () => {
    try {
      const response = await emailApi.getConnections();
      setEmailConnections(response.data.connections);
    } catch (error) {
      console.error('Failed to load email connections');
    }
  };

  const loadReferralData = async () => {
    setIsLoadingReferral(true);
    try {
      const response = await referralApi.getReferralCode();
      setReferralData(response.data);
    } catch (error) {
      console.error('Failed to load referral data');
    } finally {
      setIsLoadingReferral(false);
    }
  };

  const copyReferralCode = () => {
    if (referralData?.referral_code) {
      navigator.clipboard.writeText(referralData.referral_code);
      setCopySuccess(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const copyReferralLink = () => {
    if (referralData?.referral_code) {
      const link = `${window.location.origin}/signup?ref=${referralData.referral_code}`;
      navigator.clipboard.writeText(link);
      toast.success('Referral link copied!');
    }
  };

  // Account deletion handler
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setIsDeletingAccount(true);
    try {
      await authApi.deleteAccount();
      toast.success('Account deleted successfully');
      logout();
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setIsDeletingAccount(false);
      setDeleteAccountModal(false);
      setDeleteConfirmText('');
    }
  };

  // Load referral data on mount
  useEffect(() => {
    loadReferralData();
  }, []);

  const handleUpgrade = async (plan) => {
    setIsLoadingPayment(true);
    try {
      const originUrl = window.location.origin;
      const response = await paymentsApi.createCheckout(plan, originUrl);
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setIsLoadingPayment(false);
    }
  };

  const handleConnectGmail = async () => {
    // Start Gmail OAuth flow
    try {
      const redirectUri = `${window.location.origin}/api/auth/callback/gmail`;
      const response = await emailApi.startGmailOAuth(redirectUri);
      // Redirect to Google OAuth page
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Failed to start Gmail OAuth:', error);
      toast.error(error.response?.data?.detail || 'Failed to connect Gmail. Please try again.');
    }
  };

  const handleScanGmail = async (connectionId, email) => {
    setScanningConnectionId(connectionId);
    try {
      const response = await emailApi.scanGmail(connectionId);
      setScanResults(prev => ({ ...prev, [connectionId]: response.data }));
      
      // Show results in modal if subscriptions were found
      if (response.data.detected && response.data.detected.length > 0) {
        const detected = response.data.detected.map(sub => ({
          ...sub,
          id: sub.id || String(Math.random()),
          source: sub.source || 'email_scan'
        }));
        setEditingSubscriptions(detected);
        // Pre-select high/medium confidence subscriptions
        setSelectedSubscriptions(detected.filter(s => s.confidence !== 'low').map(s => s.id));
        setScanResultsModal({
          open: true,
          results: detected,
          scannedEmails: response.data.scanned_emails || [],
          sourceEmail: email,
          emailsScanned: response.data.emails_scanned
        });
      } else {
        toast.info(`No subscriptions found in ${email}. (Scanned ${response.data.emails_scanned} emails)`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scan Gmail');
    } finally {
      setScanningConnectionId(null);
    }
  };

  const handleDisconnectGmail = async () => {
    const { email, connectionId } = disconnectModal;
    if (!email || !connectionId) return;
    
    setDisconnectingConnectionId(connectionId);
    try {
      await emailApi.disconnectGmail(email, connectionId);
      toast.success(`${email} disconnected`);
      // Remove scan results for this connection
      setScanResults(prev => {
        const newResults = { ...prev };
        delete newResults[connectionId];
        return newResults;
      });
      loadEmailConnections();
      setDisconnectModal({ open: false, email: null, connectionId: null });
    } catch (error) {
      toast.error('Failed to disconnect Gmail');
    } finally {
      setDisconnectingConnectionId(null);
    }
  };

  const openDisconnectModal = (email, connectionId) => {
    setDisconnectModal({ open: true, email, connectionId });
  };

  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    try {
      await authApi.updateProfile({
        country: selectedCountry,
        language: selectedLanguage
      });
      setLanguage(selectedLanguage);
      updateUser({ ...user, country: selectedCountry, language: selectedLanguage });
      toast.success(t.common.success);
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Get all Gmail connections (supports multiple accounts)
  const gmailConnections = emailConnections.filter(c => c.provider === 'gmail' && c.status === 'connected');
  // Get all Outlook connections (supports multiple accounts)
  const outlookConnections = emailConnections.filter(c => c.provider === 'outlook' && c.status === 'connected');

  // Outlook connection handlers
  const [isConnectingOutlook, setIsConnectingOutlook] = useState(false);
  const [scanningOutlookId, setScanningOutlookId] = useState(null);
  const [disconnectingOutlookId, setDisconnectingOutlookId] = useState(null);
  const [outlookScanResults, setOutlookScanResults] = useState({});
  const [disconnectOutlookModal, setDisconnectOutlookModal] = useState({ open: false, email: null, connectionId: null });

  const handleConnectOutlook = async () => {
    setIsConnectingOutlook(true);
    try {
      const redirectUri = `${BACKEND_URL}/api/auth/callback/outlook`;
      const response = await emailApi.startOutlookOAuth(redirectUri);
      
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      } else {
        toast.error('Failed to get authorization URL');
        setIsConnectingOutlook(false);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to connect Outlook';
      toast.error(message);
      setIsConnectingOutlook(false);
    }
  };

  const handleScanOutlook = async (connectionId, email) => {
    setScanningOutlookId(connectionId);
    try {
      const response = await emailApi.scanOutlook(connectionId);
      setOutlookScanResults(prev => ({ ...prev, [connectionId]: response.data }));
      
      // Open scan results modal if subscriptions were detected
      if (response.data.detected && response.data.detected.length > 0) {
        setScanResultsModal({
          open: true,
          results: response.data.detected,
          scannedEmails: response.data.scanned_emails || [],
          sourceEmail: response.data.source_email,
          emailsScanned: response.data.emails_scanned
        });
      } else {
        toast.info(`Scanned ${response.data.emails_scanned} emails - no subscriptions detected`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scan Outlook');
    } finally {
      setScanningOutlookId(null);
    }
  };

  // Scan Results Modal state
  const [scanResultsModal, setScanResultsModal] = useState({ 
    open: false, 
    results: [], 
    scannedEmails: [],
    sourceEmail: '',
    emailsScanned: 0
  });
  const [editingSubscriptions, setEditingSubscriptions] = useState([]);
  const [selectedSubscriptions, setSelectedSubscriptions] = useState([]);
  const [isSavingSubscriptions, setIsSavingSubscriptions] = useState(false);

  // Initialize editing state when modal opens
  useEffect(() => {
    if (scanResultsModal.open && scanResultsModal.results.length > 0) {
      setEditingSubscriptions(scanResultsModal.results.map(sub => ({ ...sub })));
      setSelectedSubscriptions(scanResultsModal.results.map(sub => sub.id));
    }
  }, [scanResultsModal.open, scanResultsModal.results]);

  const handleUpdateSubscription = (id, field, value) => {
    setEditingSubscriptions(prev => 
      prev.map(sub => sub.id === id ? { ...sub, [field]: value } : sub)
    );
  };

  const handleToggleSubscription = (id) => {
    setSelectedSubscriptions(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSaveSelectedSubscriptions = async () => {
    const toSave = editingSubscriptions.filter(sub => selectedSubscriptions.includes(sub.id));
    if (toSave.length === 0) {
      toast.error('Please select at least one subscription to save');
      return;
    }

    setIsSavingSubscriptions(true);
    try {
      const response = await emailApi.saveScannedSubscriptions(toSave);
      toast.success(`Added ${response.data.subscriptions_created} subscription(s)!`);
      setScanResultsModal({ open: false, results: [], scannedEmails: [], sourceEmail: '', emailsScanned: 0 });
      setEditingSubscriptions([]);
      setSelectedSubscriptions([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save subscriptions');
    } finally {
      setIsSavingSubscriptions(false);
    }
  };

  const handleDisconnectOutlook = async () => {
    const { email, connectionId } = disconnectOutlookModal;
    if (!email || !connectionId) return;
    
    setDisconnectingOutlookId(connectionId);
    try {
      await emailApi.disconnectOutlook(email, connectionId);
      toast.success(`${email} disconnected`);
      setOutlookScanResults(prev => {
        const newResults = { ...prev };
        delete newResults[connectionId];
        return newResults;
      });
      loadEmailConnections();
      setDisconnectOutlookModal({ open: false, email: null, connectionId: null });
    } catch (error) {
      toast.error('Failed to disconnect Outlook');
    } finally {
      setDisconnectingOutlookId(null);
    }
  };

  // Bank Statement Scanner handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file && file.type === 'application/pdf') {
      setUploadedFile(file);
      setBankStatementResults(null);
    } else {
      toast.error('Please upload a PDF file');
    }
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setUploadedFile(file);
      setBankStatementResults(null);
    } else {
      toast.error('Please upload a PDF file');
    }
  };

  const handleScanBankStatement = async () => {
    if (!uploadedFile) return;
    
    setIsScanning(true);
    try {
      const response = await scanApi.scanBankStatement(uploadedFile);
      setBankStatementResults(response.data);
      
      if (response.data.subscriptions_detected > 0) {
        // Prepare subscriptions for review modal
        const detected = response.data.detected.map(sub => ({
          ...sub,
          id: sub.id || String(Math.random()),
          source: 'bank_statement'
        }));
        setEditingSubscriptions(detected);
        setSelectedSubscriptions(detected.filter(s => s.confidence !== 'low').map(s => s.id));
        setScanResultsModal({
          open: true,
          results: detected,
          scannedEmails: [],
          sourceEmail: `Bank Statement: ${uploadedFile.name}`,
          emailsScanned: response.data.pages_scanned
        });
      } else {
        toast.info('No subscriptions found in the bank statement');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scan bank statement');
    } finally {
      setIsScanning(false);
    }
  };

  const clearUploadedFile = () => {
    setUploadedFile(null);
    setBankStatementResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-8" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
            {t.settings.title}
          </h1>
          <p className="text-gray-500 mt-1">{t.settings.subtitle}</p>
        </div>

        {/* Account Info */}
        <Card className="border border-gray-100 shadow-soft">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <User className="w-5 h-5 text-[#064E3B]" />
              {t.settings.account}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <p className="text-sm text-gray-500">{t.settings.email}</p>
                <p className="font-medium text-gray-900">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="text-sm text-gray-500">{t.settings.memberSince}</p>
                <p className="font-medium text-gray-900">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  }) : 'N/A'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Settings */}
        <Card className="border border-gray-100 shadow-soft">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Globe className="w-5 h-5 text-[#064E3B]" />
              {t.settings.profile}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{t.settings.country}</label>
                <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                  <SelectTrigger className="h-12" data-testid="settings-country-select">
                    <SelectValue placeholder="Select country">
                      {selectedCountry && countryNames[selectedCountry]}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {supportedCountries.map((code) => (
                      <SelectItem key={code} value={code}>
                        {countryNames[code] || code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{t.settings.language}</label>
                <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                  <SelectTrigger className="h-12" data-testid="settings-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {supportedLanguages.map((code) => (
                      <SelectItem key={code} value={code}>
                        {t.languages[code]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button 
              onClick={handleSaveProfile}
              disabled={isSavingProfile}
              className="bg-[#064E3B] hover:bg-[#053D2E] text-white rounded-xl"
              data-testid="save-profile-btn"
            >
              {isSavingProfile ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {t.common.save}
            </Button>
          </CardContent>
        </Card>

        {/* Refer a Friend & Badges */}
        <Card className="border border-gray-100 shadow-soft">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Gift className="w-5 h-5 text-[#064E3B]" />
              Refer a Friend
            </CardTitle>
            <CardDescription>
              Share SubGuard with friends and earn badges & rewards
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {isLoadingReferral ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-[#064E3B]" />
              </div>
            ) : referralData ? (
              <>
                {/* Referral Code */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Your Referral Code</label>
                  <div className="flex gap-2">
                    <div className="flex-1 flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200">
                      <span className="font-mono font-bold text-lg text-[#064E3B]">
                        {referralData.referral_code}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={copyReferralCode}
                        className="h-8"
                      >
                        {copySuccess ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    <Button
                      variant="outline"
                      onClick={copyReferralLink}
                      className="rounded-xl"
                    >
                      Copy Link
                    </Button>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-[#D1FAE5]/30 rounded-xl text-center">
                    <Users className="w-6 h-6 mx-auto mb-2 text-[#064E3B]" />
                    <p className="text-2xl font-bold text-[#064E3B]">{referralData.referral_count || 0}</p>
                    <p className="text-sm text-gray-600">Friends Referred</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-xl text-center">
                    <Award className="w-6 h-6 mx-auto mb-2 text-amber-600" />
                    <p className="text-2xl font-bold text-amber-600">
                      {referralData.current_badge?.label || 'None'}
                    </p>
                    <p className="text-sm text-gray-600">Current Badge</p>
                  </div>
                </div>

                {/* Badge Progress */}
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900">Badge Milestones</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: 'supporter', label: 'Supporter', referrals: 1, color: '#10B981' },
                      { key: 'advocate', label: 'Advocate', referrals: 3, color: '#3B82F6' },
                      { key: 'guardian', label: 'Guardian', referrals: 5, color: '#8B5CF6' },
                      { key: 'legend', label: 'Legend', referrals: 10, color: '#F59E0B' }
                    ].map((badge) => {
                      const isEarned = (referralData.referral_count || 0) >= badge.referrals;
                      return (
                        <div
                          key={badge.key}
                          className={`p-3 rounded-xl border-2 transition-all ${
                            isEarned 
                              ? 'border-current bg-opacity-10' 
                              : 'border-gray-200 opacity-50'
                          }`}
                          style={{ 
                            borderColor: isEarned ? badge.color : undefined,
                            backgroundColor: isEarned ? `${badge.color}15` : undefined
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <Award 
                              className="w-5 h-5" 
                              style={{ color: isEarned ? badge.color : '#9CA3AF' }} 
                            />
                            <div>
                              <p className="font-medium text-sm" style={{ color: isEarned ? badge.color : '#6B7280' }}>
                                {badge.label}
                              </p>
                              <p className="text-xs text-gray-500">
                                {badge.referrals} referral{badge.referrals > 1 ? 's' : ''}
                              </p>
                            </div>
                            {isEarned && (
                              <CheckCircle className="w-4 h-4 ml-auto" style={{ color: badge.color }} />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Next milestone hint */}
                {referralData.next_badge && (
                  <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">Next milestone:</span> Refer {referralData.next_badge.referrals_needed} more friend{referralData.next_badge.referrals_needed > 1 ? 's' : ''} to unlock <span className="font-semibold">{referralData.next_badge.label}</span> badge!
                    </p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-gray-500 text-center py-4">Unable to load referral data</p>
            )}
          </CardContent>
        </Card>

        {/* Subscription Plan */}
        <Card className="border border-gray-100 shadow-soft">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Crown className="w-5 h-5 text-[#064E3B]" />
              {t.settings.subscriptionPlan}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Current Plan - Enhanced Display */}
            <div className={`p-5 rounded-xl border-2 ${
              user?.subscription_plan === 'founding' 
                ? 'bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6f] border-[#6ee7b7] text-white'
                : user?.subscription_plan 
                ? 'bg-[#D1FAE5] border-[#064E3B]'
                : quickCheckStatus?.has_quick_check
                ? 'bg-orange-50 border-orange-300'
                : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                    user?.subscription_plan === 'founding'
                      ? 'bg-[#6ee7b7]/20'
                      : user?.subscription_plan 
                      ? 'bg-white'
                      : quickCheckStatus?.has_quick_check
                      ? 'bg-orange-100'
                      : 'bg-gray-200'
                  }`}>
                    {user?.subscription_plan === 'founding' ? (
                      <Zap className="w-7 h-7 text-[#6ee7b7]" />
                    ) : user?.subscription_plan ? (
                      <Crown className="w-7 h-7 text-[#064E3B]" />
                    ) : quickCheckStatus?.has_quick_check ? (
                      <Search className="w-7 h-7 text-orange-600" />
                    ) : (
                      <AlertCircle className="w-7 h-7 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide opacity-70 mb-1">Current Plan</p>
                    <p className={`text-xl font-bold ${user?.subscription_plan === 'founding' ? 'text-white' : ''}`}>
                      {user?.subscription_plan === 'founding'
                        ? 'Founding Guard (Lifetime)'
                        : user?.subscription_plan === 'yearly'
                        ? 'Yearly Plan'
                        : user?.subscription_plan === 'monthly'
                        ? 'Monthly Plan'
                        : quickCheckStatus?.has_quick_check
                        ? 'Quick Check'
                        : 'Free Plan'
                      }
                    </p>
                    <p className={`text-sm ${user?.subscription_plan === 'founding' ? 'text-blue-100' : 'text-gray-500'}`}>
                      {user?.subscription_plan === 'founding'
                        ? 'Full access forever • No recurring fees'
                        : user?.subscription_plan === 'yearly'
                        ? '$29/year • Full access to all features'
                        : user?.subscription_plan === 'monthly'
                        ? '$2.99/month • Full access to all features'
                        : quickCheckStatus?.has_quick_check
                        ? `Expires: ${new Date(quickCheckStatus.expires_at).toLocaleDateString()}`
                        : 'Limited features • Upgrade for full access'
                      }
                    </p>
                  </div>
                </div>
                <Badge className={`text-sm px-3 py-1 ${
                  user?.subscription_plan === 'founding'
                    ? 'bg-[#6ee7b7] text-[#064E3B]'
                    : user?.subscription_plan
                    ? 'bg-[#064E3B] text-white'
                    : quickCheckStatus?.has_quick_check
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-300 text-gray-700'
                }`}>
                  {user?.subscription_plan || quickCheckStatus?.has_quick_check ? 'Active' : 'Free'}
                </Badge>
              </div>
            </div>

            {/* Quick Check Info */}
            {quickCheckStatus?.has_quick_check && !user?.subscription_plan && (
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <Search className="w-5 h-5 text-orange-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-orange-900">Quick Check Active</p>
                    <p className="text-sm text-orange-700">
                      You have 30-day scan access. Expires on {new Date(quickCheckStatus.expires_at).toLocaleDateString()}.
                      Upgrade to a full plan for unlimited access.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Upgrade Options */}
            {!user?.subscription_plan && (
              <div className="space-y-6">
                {/* Founding Guard Banner */}
                {foundingSlots.remaining > 0 && (
                  <div className="p-6 bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6f] rounded-xl text-white">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#6ee7b7]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Zap className="w-6 h-6 text-[#6ee7b7]" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold">Founding Guard — Limited Early Access</h3>
                        </div>
                        <p className="text-blue-100 text-sm mb-4">
                          One-time $49 payment for lifetime core features. Available for a short time only.
                        </p>
                        <Button
                          onClick={() => handleUpgrade('founding')}
                          disabled={isLoadingPayment}
                          className="bg-white text-[#1e3a5f] hover:bg-blue-50 rounded-xl"
                          data-testid="upgrade-founding-btn"
                        >
                          {isLoadingPayment ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Claim Early Access — $49'
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Standard Plans */}
                <div className="grid md:grid-cols-2 gap-4">
                {/* Monthly */}
                <div className="border border-gray-200 rounded-xl p-6 space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-900">Monthly</h3>
                    <p className="text-3xl font-bold text-gray-900 mt-2">
                      $2.99<span className="text-sm font-normal text-gray-500">/month</span>
                    </p>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#064E3B]" />
                      Unlimited subscriptions
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#064E3B]" />
                      AI cancellation assistant
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#064E3B]" />
                      Email scanning (60 days)
                    </li>
                  </ul>
                  <Button
                    onClick={() => handleUpgrade('monthly')}
                    disabled={isLoadingPayment}
                    className="w-full bg-[#064E3B] hover:bg-[#053D2E] text-white rounded-xl"
                    data-testid="upgrade-monthly-btn"
                  >
                    {isLoadingPayment ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Upgrade — $2.99/mo'
                    )}
                  </Button>
                </div>

                {/* Yearly */}
                <div className="border-2 border-[#064E3B] rounded-xl p-6 space-y-4 relative">
                  <Badge className="absolute -top-3 right-4 bg-[#EA580C] text-white border-0">
                    Best Value
                  </Badge>
                  <div>
                    <h3 className="font-semibold text-gray-900">Yearly</h3>
                    <p className="text-3xl font-bold text-gray-900 mt-2">
                      $29<span className="text-sm font-normal text-gray-500">/year</span>
                    </p>
                    <p className="text-xs text-green-600 mt-1">Save ~$7 vs monthly</p>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#064E3B]" />
                      Everything in Monthly
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#064E3B]" />
                      ~2 months free
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#064E3B]" />
                      Priority support
                    </li>
                  </ul>
                  <Button
                    onClick={() => handleUpgrade('yearly')}
                    disabled={isLoadingPayment}
                    className="w-full bg-[#064E3B] hover:bg-[#053D2E] text-white rounded-xl"
                    data-testid="upgrade-yearly-btn"
                  >
                    {isLoadingPayment ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Upgrade — $29/year'
                    )}
                  </Button>
                </div>
              </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Email Connections */}
        <Card className="border border-gray-100 shadow-soft">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  <Mail className="w-5 h-5 text-[#064E3B]" />
                  {t.settings.emailConnections}
                </CardTitle>
                <CardDescription>
                  {t.settings.emailConnectionsDesc} (Last 60 days scanned)
                </CardDescription>
              </div>
              {/* Add Email Account Buttons */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={handleConnectGmail}
                  disabled={isConnectingGmail}
                  className="rounded-xl relative"
                  data-testid="add-gmail-btn"
                >
                  {isConnectingGmail ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  Add Gmail
                  <Badge variant="outline" className="ml-2 text-amber-600 border-amber-300 text-[10px]">
                    Pending Verification
                  </Badge>
                </Button>
                <Button
                  variant="outline"
                  onClick={handleConnectOutlook}
                  disabled={isConnectingOutlook}
                  className="rounded-xl"
                  data-testid="add-outlook-btn"
                >
                  {isConnectingOutlook ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  )}
                  Add Outlook
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Connected Gmail Accounts */}
            {gmailConnections.length > 0 ? (
              gmailConnections.map((connection) => (
                <div key={connection.id} className="p-4 border border-gray-200 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                          <path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6zm-2 0l-8 5-8-5h16zm0 12H4V8l8 5 8-5v10z" fill="#EA4335"/>
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{connection.email}</p>
                        <p className="text-sm text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Connected
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleScanGmail(connection.id, connection.email)}
                        disabled={scanningConnectionId === connection.id}
                        className="rounded-lg"
                        data-testid={`scan-gmail-${connection.id}`}
                      >
                        {scanningConnectionId === connection.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Search className="w-4 h-4 mr-1" />
                            Scan
                          </>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDisconnectModal(connection.email, connection.id)}
                        disabled={disconnectingConnectionId === connection.id}
                        className="rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`disconnect-gmail-${connection.id}`}
                      >
                        {disconnectingConnectionId === connection.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Unplug className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Scan Results for this connection */}
                  {scanResults[connection.id] && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Scan Results: {scanResults[connection.id].emails_scanned} emails scanned (Last 60 days)
                      </p>
                      {scanResults[connection.id].subscriptions_detected?.length > 0 ? (
                        <div className="space-y-2">
                          {scanResults[connection.id].subscriptions_detected.map((sub, index) => (
                            <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                              <span className="font-medium">{sub.name}</span>
                              <span className="text-gray-600">
                                ${sub.amount}/{sub.billing_cycle}
                              </span>
                            </div>
                          ))}
                          <p className="text-xs text-gray-500 mt-2">
                            Go to Subscriptions to add these to your tracking list.
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No subscriptions detected in recent emails.</p>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="p-6 border border-dashed border-gray-300 rounded-xl text-center">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Mail className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-gray-500 font-medium">No Gmail accounts connected</p>
                <p className="text-sm text-gray-400 mt-1">Connect your Gmail to scan for subscriptions</p>
              </div>
            )}

            {/* Connected Outlook Accounts */}
            {outlookConnections.length > 0 ? (
              outlookConnections.map((connection) => (
                <div key={connection.id} className="p-4 border border-gray-200 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#0078D4" strokeWidth="2"/>
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{connection.email}</p>
                        <p className="text-sm text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Connected
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleScanOutlook(connection.id, connection.email)}
                        disabled={scanningOutlookId === connection.id}
                        className="rounded-lg"
                        data-testid={`scan-outlook-${connection.id}`}
                      >
                        {scanningOutlookId === connection.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Search className="w-4 h-4 mr-1" />
                            Scan
                          </>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDisconnectOutlookModal({ open: true, email: connection.email, connectionId: connection.id })}
                        disabled={disconnectingOutlookId === connection.id}
                        className="rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`disconnect-outlook-${connection.id}`}
                      >
                        {disconnectingOutlookId === connection.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Unplug className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Scan Results for this connection */}
                  {outlookScanResults[connection.id] && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Scan Results: {outlookScanResults[connection.id].emails_scanned} emails scanned
                      </p>
                      {outlookScanResults[connection.id].subscriptions_detected > 0 ? (
                        <div className="space-y-2">
                          <p className="text-sm text-green-600">
                            Found {outlookScanResults[connection.id].subscriptions_detected} potential subscriptions
                          </p>
                          <p className="text-xs text-gray-500">
                            Go to Subscriptions to add these to your tracking list.
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No subscriptions detected in recent emails.</p>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="p-6 border border-dashed border-gray-300 rounded-xl text-center">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Mail className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-gray-500 font-medium">No Outlook accounts connected</p>
                <p className="text-sm text-gray-400 mt-1">Connect your Outlook to scan for subscriptions</p>
                <Button
                  onClick={handleConnectOutlook}
                  disabled={isConnectingOutlook}
                  className="mt-4 bg-[#0078D4] hover:bg-[#106ebe] text-white rounded-xl"
                  data-testid="connect-outlook-btn"
                >
                  {isConnectingOutlook ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  Connect Outlook
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bank Statement Scanner */}
        <Card className="border border-gray-100 shadow-soft">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <FileText className="w-5 h-5 text-[#064E3B]" />
              Bank Statement Scanner
            </CardTitle>
            <CardDescription>
              Upload a PDF bank statement to find recurring subscriptions. Our AI will analyze your transactions.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Drop Zone */}
            <div
              className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                isDragging 
                  ? 'border-[#064E3B] bg-[#D1FAE5]/30' 
                  : uploadedFile 
                  ? 'border-[#064E3B] bg-[#D1FAE5]/10' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              data-testid="bank-statement-dropzone"
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".pdf"
                className="hidden"
                data-testid="bank-statement-file-input"
              />
              
              {uploadedFile ? (
                <div className="space-y-4">
                  <div className="w-16 h-16 bg-[#D1FAE5] rounded-xl flex items-center justify-center mx-auto">
                    <FileText className="w-8 h-8 text-[#064E3B]" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{uploadedFile.name}</p>
                    <p className="text-sm text-gray-500">
                      {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <div className="flex items-center justify-center gap-3">
                    <Button
                      onClick={handleScanBankStatement}
                      disabled={isScanning}
                      className="bg-[#064E3B] hover:bg-[#053D2E] text-white rounded-xl"
                      data-testid="scan-bank-statement-btn"
                    >
                      {isScanning ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Search className="w-4 h-4 mr-2" />
                          Scan for Subscriptions
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={clearUploadedFile}
                      disabled={isScanning}
                      className="rounded-xl"
                      data-testid="clear-file-btn"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <div 
                  className="space-y-4 cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mx-auto">
                    <Upload className="w-8 h-8 text-gray-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">
                      {isDragging ? 'Drop your PDF here' : 'Drag & drop your bank statement'}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      or click to browse • PDF files only • Max 10MB
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Scan Results Summary */}
            {bankStatementResults && (
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#D1FAE5] rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#064E3B]" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Scan Complete</p>
                    <p className="text-sm text-gray-600">
                      Scanned {bankStatementResults.pages_scanned} of {bankStatementResults.total_pages} pages
                      {bankStatementResults.limited_scan && (
                        <span className="text-amber-600"> (Free tier limit - upgrade for full scan)</span>
                      )}
                    </p>
                    <p className="text-sm text-[#064E3B] font-medium mt-1">
                      Found {bankStatementResults.subscriptions_detected} potential subscription{bankStatementResults.subscriptions_detected !== 1 ? 's' : ''}
                      {bankStatementResults.limited_results && (
                        <span className="text-amber-600"> (Free tier shows max 5 - upgrade for all results)</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* How it works */}
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
              <h4 className="font-medium text-blue-900 mb-2">How it works</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">1.</span>
                  Upload your bank statement PDF
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">2.</span>
                  Our AI analyzes transactions for recurring patterns
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">3.</span>
                  Review and add detected subscriptions to your tracker
                </li>
              </ul>
              <p className="text-xs text-blue-600 mt-3">
                🔒 Your file is processed securely and never stored on our servers
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Support - Only for paid users */}
        {user && (user.subscription_plan || user.has_quick_check) && (
          <Card className="border border-gray-100 shadow-soft">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <HelpCircle className="w-5 h-5 text-[#064E3B]" />
                Support
              </CardTitle>
              <CardDescription>
                Need help? We're here for you.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-[#D1FAE5]/30 rounded-xl border border-[#D1FAE5]">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#064E3B] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Email Support</p>
                    <p className="text-sm text-gray-600 mb-2">
                      Have questions or need assistance? Reach out to us directly.
                    </p>
                    <a 
                      href="mailto:hello@subguardhq.com" 
                      className="inline-flex items-center gap-2 text-[#064E3B] font-medium hover:underline"
                    >
                      hello@subguardhq.com
                    </a>
                    <p className="text-xs text-gray-500 mt-2">
                      We typically respond within 24 hours
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Danger Zone - Account Deletion */}
        <Card className="border border-red-200 shadow-soft">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-red-600" style={{ fontFamily: 'Outfit' }}>
              <Trash2 className="w-5 h-5" />
              Danger Zone
            </CardTitle>
            <CardDescription>
              Irreversible actions for your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-red-50 rounded-xl border border-red-200">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium text-red-800">Delete Account</p>
                  <p className="text-sm text-red-600 mt-1">
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setDeleteAccountModal(true)}
                  className="border-red-300 text-red-600 hover:bg-red-100 hover:text-red-700 flex-shrink-0"
                  data-testid="delete-account-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Account
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Disconnect Gmail Confirmation Modal */}
      <Dialog open={disconnectModal.open} onOpenChange={(open) => !open && setDisconnectModal({ open: false, email: null, connectionId: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Unplug className="w-5 h-5" />
              Disconnect Gmail Account
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to disconnect this Gmail account? You can reconnect it later if needed.
            </DialogDescription>
          </DialogHeader>
          {disconnectModal.email && (
            <div className="py-4">
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <Mail className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{disconnectModal.email}</p>
                  <p className="text-sm text-gray-500">Gmail Account</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setDisconnectModal({ open: false, email: null, connectionId: null })}
              disabled={disconnectingConnectionId !== null}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDisconnectGmail}
              disabled={disconnectingConnectionId !== null}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-disconnect-btn"
            >
              {disconnectingConnectionId !== null ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Disconnecting...</>
              ) : (
                <>
                  <Unplug className="w-4 h-4 mr-2" />
                  Disconnect
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disconnect Outlook Confirmation Modal */}
      <Dialog open={disconnectOutlookModal.open} onOpenChange={(open) => !open && setDisconnectOutlookModal({ open: false, email: null, connectionId: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Unplug className="w-5 h-5" />
              Disconnect Outlook Account
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to disconnect this Outlook account? You can reconnect it later if needed.
            </DialogDescription>
          </DialogHeader>
          {disconnectOutlookModal.email && (
            <div className="py-4">
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#0078D4" strokeWidth="2"/>
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{disconnectOutlookModal.email}</p>
                  <p className="text-sm text-gray-500">Outlook Account</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setDisconnectOutlookModal({ open: false, email: null, connectionId: null })}
              disabled={disconnectingOutlookId !== null}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDisconnectOutlook}
              disabled={disconnectingOutlookId !== null}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-disconnect-outlook-btn"
            >
              {disconnectingOutlookId !== null ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Disconnecting...</>
              ) : (
                <>
                  <Unplug className="w-4 h-4 mr-2" />
                  Disconnect
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scan Results Modal */}
      <Dialog open={scanResultsModal.open} onOpenChange={(open) => !open && setScanResultsModal({ open: false, results: [], scannedEmails: [], sourceEmail: '', emailsScanned: 0 })}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#064E3B] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Search className="w-5 h-5" />
              Scan Results
            </DialogTitle>
            <DialogDescription>
              {scanResultsModal.sourceEmail.includes('Bank Statement') 
                ? `Scanned ${scanResultsModal.emailsScanned} page(s) from ${scanResultsModal.sourceEmail}.`
                : `Scanned ${scanResultsModal.emailsScanned} emails from ${scanResultsModal.sourceEmail}.`
              }
              {' '}Review and edit detected subscriptions before adding them.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {editingSubscriptions.length > 0 ? (
              editingSubscriptions.map((sub, index) => (
                <div 
                  key={sub.id} 
                  className={`p-4 border rounded-xl transition-all ${
                    selectedSubscriptions.includes(sub.id) 
                      ? 'border-[#064E3B] bg-[#D1FAE5]/30' 
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedSubscriptions.includes(sub.id)}
                      onChange={() => handleToggleSubscription(sub.id)}
                      className="mt-1 w-5 h-5 rounded border-gray-300 text-[#064E3B] focus:ring-[#064E3B]"
                    />
                    <div className="flex-1 space-y-3">
                      {/* Name */}
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Service Name</label>
                        <input
                          type="text"
                          value={sub.name || ''}
                          onChange={(e) => handleUpdateSubscription(sub.id, 'name', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#064E3B] focus:border-transparent"
                          placeholder="e.g., Netflix, Spotify"
                        />
                      </div>
                      
                      {/* Amount and Currency */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Amount</label>
                          <input
                            type="number"
                            step="0.01"
                            value={sub.amount || ''}
                            onChange={(e) => handleUpdateSubscription(sub.id, 'amount', parseFloat(e.target.value) || 0)}
                            className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#064E3B] focus:border-transparent"
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Currency</label>
                          <select
                            value={sub.currency || 'USD'}
                            onChange={(e) => handleUpdateSubscription(sub.id, 'currency', e.target.value)}
                            className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#064E3B] focus:border-transparent"
                          >
                            <option value="USD">USD ($)</option>
                            <option value="EUR">EUR (€)</option>
                            <option value="GBP">GBP (£)</option>
                            <option value="AUD">AUD (A$)</option>
                            <option value="CAD">CAD (C$)</option>
                          </select>
                        </div>
                      </div>
                      
                      {/* Billing Cycle */}
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Billing Cycle</label>
                        <select
                          value={sub.billing_cycle || 'monthly'}
                          onChange={(e) => handleUpdateSubscription(sub.id, 'billing_cycle', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#064E3B] focus:border-transparent"
                        >
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="yearly">Yearly</option>
                        </select>
                      </div>
                      
                      {/* Confidence + Category for Bank Statement and Email Scans */}
                      {(sub.source === 'bank_statement' || sub.source === 'email_scan') && (sub.confidence || sub.category) && (
                        <div className="flex items-center gap-3 flex-wrap">
                          {sub.confidence && (
                            <Badge className={`text-xs ${
                              sub.confidence === 'high' ? 'bg-green-100 text-green-700' :
                              sub.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {sub.confidence} confidence
                            </Badge>
                          )}
                          {sub.category && (
                            <Badge variant="outline" className="text-xs">
                              {sub.category}
                            </Badge>
                          )}
                        </div>
                      )}
                      
                      {/* Source Email Subject */}
                      {sub.source_subject && (
                        <div className="text-xs text-gray-500 bg-gray-100 p-2 rounded">
                          <span className="font-medium">Source:</span> {sub.source_subject}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No subscriptions detected in the scanned emails.
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setScanResultsModal({ open: false, results: [], scannedEmails: [], sourceEmail: '', emailsScanned: 0 })}
              disabled={isSavingSubscriptions}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveSelectedSubscriptions}
              disabled={isSavingSubscriptions || selectedSubscriptions.length === 0}
              className="bg-[#064E3B] hover:bg-[#053D2E] text-white"
              data-testid="save-subscriptions-btn"
            >
              {isSavingSubscriptions ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Add {selectedSubscriptions.length} Subscription{selectedSubscriptions.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Confirmation Modal */}
      <Dialog open={deleteAccountModal} onOpenChange={setDeleteAccountModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Trash2 className="w-5 h-5" />
              Delete Account
            </DialogTitle>
            <DialogDescription>
              This action is permanent and cannot be undone. All your data will be deleted.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="p-4 bg-red-50 rounded-xl border border-red-200">
              <p className="text-sm text-red-800 font-medium mb-2">The following data will be permanently deleted:</p>
              <ul className="text-sm text-red-700 space-y-1">
                <li>• All your subscriptions and tracking data</li>
                <li>• Email connections and scan history</li>
                <li>• Bank statement scan results</li>
                <li>• Cancellation workflows and notes</li>
                <li>• Referral history and badges</li>
                <li>• Your account and profile</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Type <span className="font-mono bg-gray-100 px-1 rounded">DELETE</span> to confirm:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE to confirm"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                data-testid="delete-confirm-input"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => {
                setDeleteAccountModal(false);
                setDeleteConfirmText('');
              }}
              disabled={isDeletingAccount}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteAccount}
              disabled={isDeletingAccount || deleteConfirmText !== 'DELETE'}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-account-btn"
            >
              {isDeletingAccount ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete My Account
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Settings;
